import { CaretRight, ClockCounterClockwise } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";
import { type AuditEntry, type AuditFilters, auditQuery, usersQuery } from "@/components/admin/api";
import { AUDIT_CATEGORIES, auditActionLabel, auditSourceLabel, auditTargetLabel } from "@/components/admin/audit";
import { adminError, isForbidden } from "@/components/admin/errors";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Avatar,
  Badge,
  Button,
  ComboBox,
  CopyButton,
  DateButton,
  dataTableStyles,
  EmptyState,
  InlineError,
  RelativeTime,
  Select,
  TableSkeleton,
} from "@/components/ui";
import { cx } from "@/lib/cx";
import { fullDateTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/audit")({ component: AuditPage });

const ANY_ACTOR = "__any";

/** Local calendar day to an instant: start of the day for `from`, last millisecond for `to`. */
const dayStart = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const dayEnd = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();

function AuditPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_audit() }]);
  const [category, setCategory] = useState("");
  const [actor, setActor] = useState(ANY_ACTOR);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const users = useQuery({ ...usersQuery, retry: false });

  const filters: AuditFilters = useMemo(
    () => ({
      ...(category ? { action: category } : {}),
      ...(actor !== ANY_ACTOR ? { actorId: actor } : {}),
      ...(from ? { from: dayStart(from) } : {}),
      ...(to ? { to: dayEnd(to) } : {}),
    }),
    [category, actor, from, to],
  );
  const filtered = Object.keys(filters).length > 0;
  const q = useInfiniteQuery(auditQuery(filters));
  const entries = q.data?.pages.flatMap((p) => p.items) ?? [];

  const people = [
    { id: ANY_ACTOR, label: m.adm_audit_any_actor() },
    ...(users.data ?? []).map((u) => ({
      id: u.id,
      label: u.kind === "bot" ? u.name : `${u.name} (${u.email})`,
      textValue: `${u.name} ${u.email}`,
    })),
  ];

  return (
    <Page
      title={m.adm_nav_audit()}
      icon={ClockCounterClockwise}
      meta={m.adm_audit_meta()}
      width="full"
      className="max-w-5xl"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label={m.adm_audit_filter_action()}
            className="w-48"
            items={AUDIT_CATEGORIES.map((c) => ({ id: c.id || "__all", label: c.label() }))}
            selectedKey={category || "__all"}
            onSelectionChange={(k) => setCategory(k === "__all" ? "" : k)}
          />
          <ComboBox
            label={m.adm_audit_filter_actor()}
            className="w-56"
            items={people}
            selectedKey={actor}
            onSelectionChange={(k) => setActor(k ?? ANY_ACTOR)}
            isDisabled={users.isError}
          />
          <div className="flex flex-col gap-(--gap-field)">
            <span className="text-sm font-medium text-fg">{m.adm_audit_from()}</span>
            <div className="h-(--input-h) w-40 rounded-control border border-input-border bg-input-bg">
              <DateButton
                value={from}
                onChange={setFrom}
                label={m.adm_audit_from()}
                placeholder={m.adm_audit_from_placeholder()}
                className="h-full"
              />
            </div>
          </div>
          <div className="flex flex-col gap-(--gap-field)">
            <span className="text-sm font-medium text-fg">{m.adm_audit_to()}</span>
            <div className="h-(--input-h) w-40 rounded-control border border-input-border bg-input-bg">
              <DateButton
                value={to}
                onChange={setTo}
                label={m.adm_audit_to()}
                placeholder={m.adm_audit_to_placeholder()}
                className="h-full"
              />
            </div>
          </div>
          {filtered ? (
            <Button
              variant="ghost"
              onPress={() => {
                setCategory("");
                setActor(ANY_ACTOR);
                setFrom(null);
                setTo(null);
              }}
            >
              {m.adm_audit_clear()}
            </Button>
          ) : null}
        </div>

        {q.isPending ? (
          <TableSkeleton rows={8} columns={4} />
        ) : q.isError ? (
          isForbidden(q.error) ? (
            <EmptyState
              title={m.adm_section_denied_title()}
              description={m.adm_section_denied_body({ permission: "audit.read" })}
            />
          ) : (
            <InlineError message={adminError(q.error)} onRetry={() => void q.refetch()} isRetrying={q.isFetching} />
          )
        ) : entries.length === 0 ? (
          <div className={cx(dataTableStyles.frame, "px-4 py-8")}>
            <EmptyState
              title={filtered ? m.adm_audit_none_title() : m.adm_audit_empty_title()}
              description={filtered ? m.adm_audit_none_body() : m.adm_audit_empty_body()}
            />
          </div>
        ) : (
          <>
            <div className={dataTableStyles.frame}>
              <table className={dataTableStyles.table}>
                <caption className="sr-only">{m.adm_audit_table()}</caption>
                <thead className={dataTableStyles.head}>
                  <tr>
                    <th scope="col" className={cx(dataTableStyles.th, "w-48")}>
                      {m.adm_audit_col_when()}
                    </th>
                    <th scope="col" className={dataTableStyles.th}>
                      {m.adm_audit_col_actor()}
                    </th>
                    <th scope="col" className={dataTableStyles.th}>
                      {m.adm_audit_col_action()}
                    </th>
                    <th scope="col" className={cx(dataTableStyles.th, "hidden md:table-cell")}>
                      {m.adm_audit_col_target()}
                    </th>
                  </tr>
                </thead>
                {entries.map((e) => (
                  <AuditRow key={e.id} entry={e} />
                ))}
              </table>
            </div>
            {q.hasNextPage ? (
              <div className="flex justify-center">
                <Button variant="secondary" isPending={q.isFetchingNextPage} onPress={() => void q.fetchNextPage()}>
                  {m.ui_load_more()}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Page>
  );
}

/** One short phrase for the target: the most telling field of the entry's data, else type and short id. */
function targetSummary(e: AuditEntry): string {
  const d = e.data;
  const pick = ["email", "name", "slug", "role", "key", "space"]
    .map((k) => d[k])
    .find((v) => typeof v === "string" && v);
  if (typeof pick === "string") return pick;
  if (!e.targetType) return "-";
  const type = auditTargetLabel(e.targetType);
  // People acting on themselves (sign-in, password change): their name, not an id.
  if (e.targetType === "user" && e.targetId && e.targetId === e.actorId && e.actor?.name) return e.actor.name;
  // The instance is a singleton and "all" is not an id: the label says everything.
  if (e.targetType === "instance" || !e.targetId) return type;
  if (e.targetType === "translation_provider" && e.targetId === "all") return m.audit_target_all_providers();
  return m.adm_audit_target_type({ type, id: e.targetId.slice(0, 8) });
}

function AuditRow({ entry: e }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const panelId = `audit-${e.id}`;
  const label = auditActionLabel(e.action);
  const actorName = e.actor?.name ?? (e.actorId ? m.adm_user_unknown() : m.adm_system_actor());
  return (
    <tbody className="border-b border-line last:border-b-0">
      <tr className={cx("transition-ui hover:bg-hover", open && "bg-subtle")}>
        <td className={cx(dataTableStyles.td, "py-1")}>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`${label}, ${fullDateTime(e.createdAt)}. ${open ? m.ui_hide_details() : m.ui_show_details()}`}
            onClick={() => setOpen((o) => !o)}
            className="transition-ui flex min-h-(--control-h-md) w-full items-center gap-2 rounded-control text-left outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
          >
            <CaretRight
              size={12}
              weight="bold"
              aria-hidden="true"
              className={cx("shrink-0 text-fg-subtle transition-transform duration-(--dur-fast)", open && "rotate-90")}
            />
            <span className="flex flex-col whitespace-nowrap">
              <RelativeTime value={e.createdAt} className="text-fg" />
              <span className="text-xs text-fg-subtle tnum">{fullDateTime(e.createdAt)}</span>
            </span>
          </button>
        </td>
        <td className={dataTableStyles.td}>
          <div className="flex min-w-0 items-center gap-2">
            <Avatar
              name={actorName}
              seed={e.actorId ?? "system"}
              bot={e.actor?.kind === "bot"}
              size="sm"
              labelled={false}
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate-title text-fg" title={actorName}>
                {actorName}
              </span>
              {e.via?.name ? (
                <span className="text-xs text-fg-subtle">{m.adm_audit_via({ name: e.via.name })}</span>
              ) : null}
            </div>
          </div>
        </td>
        <td className={dataTableStyles.td}>
          <span className="text-fg" title={e.action}>
            {label}
          </span>
        </td>
        <td className={cx(dataTableStyles.td, "hidden md:table-cell")}>
          <span className="truncate-title block max-w-56 text-fg-muted" title={targetSummary(e)}>
            {targetSummary(e)}
          </span>
        </td>
      </tr>
      {open ? (
        <tr id={panelId} className="bg-subtle">
          <td colSpan={4} className="px-4 pt-1 pb-4">
            <Details entry={e} actorName={actorName} />
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

function Details({ entry: e, actorName }: { entry: AuditEntry; actorName: string }) {
  const hasData = Object.keys(e.data).length > 0;
  const json = JSON.stringify(e.data, null, 2);
  const rows: Array<[string, ReactNode]> = [
    [m.adm_audit_details_time(), fullDateTime(e.createdAt)],
    [
      m.adm_audit_details_code(),
      <code key="c" className="font-mono text-xs">
        {e.action}
      </code>,
    ],
    [
      m.adm_audit_details_actor(),
      <span key="a">
        {actorName}
        {e.actorId ? <span className="ml-2 font-mono text-xs text-fg-subtle">{e.actorId}</span> : null}
      </span>,
    ],
    ...(e.targetType
      ? ([
          [
            m.adm_audit_details_target(),
            <span key="t">
              {auditTargetLabel(e.targetType)}
              {e.targetId ? <span className="ml-2 font-mono text-xs text-fg-subtle">{e.targetId}</span> : null}
            </span>,
          ],
        ] as Array<[string, ReactNode]>)
      : []),
    [m.adm_audit_details_source(), <Badge key="s">{auditSourceLabel(e.source)}</Badge>],
    ...(e.ip
      ? ([
          [
            m.adm_audit_details_ip(),
            <span key="i" className="font-mono text-xs">
              {e.ip}
            </span>,
          ],
        ] as Array<[string, ReactNode]>)
      : []),
    ...(e.userAgent
      ? ([
          [
            m.adm_audit_details_agent(),
            <span key="u" className="text-xs break-all text-fg-muted">
              {e.userAgent}
            </span>,
          ],
        ] as Array<[string, ReactNode]>)
      : []),
  ];
  return (
    <div className="flex flex-col gap-3 pl-5">
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-fg-muted">{k}</dt>
            <dd className="m-0 min-w-0 text-fg">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-xs font-medium text-fg-muted">{m.adm_audit_details_data()}</h4>
          {hasData ? <CopyButton value={json} size="sm" variant="ghost" label={m.adm_audit_details_data()} /> : null}
        </div>
        {hasData ? (
          <pre className="scroll-thin m-0 max-h-64 overflow-auto rounded-control border border-line bg-surface p-3 font-mono text-xs leading-5 text-fg">
            {json}
          </pre>
        ) : (
          <p className="text-sm text-fg-muted">{m.adm_audit_details_none()}</p>
        )}
      </div>
    </div>
  );
}
